import { Application, User, SessionInfo } from '../types';
import { Activity, AlertCircle, CheckCircle, Loader, Clock, User as UserIcon } from 'lucide-react';
import './StatusBar.css';

interface StatusBarProps {
  application: Application | null;
  currentSequence: string | null;
  viewMode: { type: 'cumulative' | 'current' };
  documentCount: number;
  isLoading: boolean;
  error: string | null;
  user: User;
  sessionInfo: SessionInfo | null;
  regionalInfo?: {
    country: string;
    agency: string;
    dtdVersion: string;
    regionCode: string;
  } | undefined;
}

export function StatusBar({
  application,
  currentSequence,
  viewMode,
  documentCount,
  isLoading,
  error,
  user,
  sessionInfo,
  regionalInfo,
}: StatusBarProps) {
  
  const getStatusMessage = () => {
    if (error) {
      return {
        icon: <AlertCircle className="status-icon error" />,
        text: `Error: ${error}`,
        type: 'error' as const,
      };
    }

    if (isLoading) {
      return {
        icon: <Loader className="status-icon loading" />,
        text: 'Loading application...',
        type: 'loading' as const,
      };
    }

    if (!application) {
      return {
        icon: <Activity className="status-icon" />,
        text: 'No application loaded. Select an eCTD folder to get started.',
        type: 'idle' as const,
      };
    }

    const viewText = viewMode.type === 'cumulative' ? 'Cumulative' : 'Current';
    const sequenceText = viewMode.type === 'current' && currentSequence ? ` (Sequence ${currentSequence})` : '';
    
    return {
      icon: <CheckCircle className="status-icon success" />,
      text: `${viewText} View${sequenceText} — ${documentCount} document${documentCount !== 1 ? 's' : ''}`,
      type: 'success' as const,
    };
  };

  const status = getStatusMessage();

  const formatSessionDuration = (sessionInfo: SessionInfo) => {
    const duration = Date.now() - sessionInfo.startTime.getTime();
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className={`status-bar ${status.type}`}>
      <div className="status-content">
        {status.icon}
        <span className="status-text">{status.text}</span>
 
        {regionalInfo && (
          <span className="regional-text">
            <span className="regional-agency">{regionalInfo.agency}</span>
            <span className="regional-dtd">DTD {regionalInfo.dtdVersion}</span>
          </span>
        )}
      </div>
 
      <div className="status-info">
        {application && (
          <span className="app-info">{application.id}</span>
        )}
 
        {sessionInfo && (
          <div className="session-info">
            <UserIcon className="session-icon" />
            <span className="user-name">{user.displayName || user.email.split('@')[0]}</span>
            <Clock className="session-icon" />
            <span className="session-duration">{formatSessionDuration(sessionInfo)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

